// Mirror of backend/dealers-service/app/Enums/DealerType.php
//   (allowedOwnershipParent(), requiresOwnershipParent()).
// KEEP IN SYNC: change this table only alongside that enum; the pinning test
// below fails loudly if a row drifts. If the matrix starts changing often,
// promote to a backend-served GET /dealers/type-rules (see
// docs/roadmap/7.1.2-dealer-ui-type-aware-ownership.md Section 4.1).
import type { DealerType } from '../types/dealers.types';

interface ParentageRule {
  allowedParent: DealerType | null; // owning-parent type, or null if none permitted
  parentRequired: boolean;          // must an owning parent be set?
}

export const dealerParentageRules: Record<DealerType, ParentageRule> = {
  oem_group:        { allowedParent: 'oem_group',    parentRequired: false },
  oem:              { allowedParent: 'oem_group',    parentRequired: true  },
  dealer_group:     { allowedParent: 'dealer_group', parentRequired: false },
  franchise_dealer: { allowedParent: 'dealer_group', parentRequired: true  },
  dealer:           { allowedParent: 'dealer_group', parentRequired: false },
};

export const allowedParentType = (type: DealerType): DealerType | null =>
  dealerParentageRules[type].allowedParent;

export const parentIsRequired = (type: DealerType): boolean =>
  dealerParentageRules[type].parentRequired;

// A type can own children iff it appears as some type's allowed parent.
// Derived, not hand-listed, so it can never drift from the matrix.
const ownableParentTypes = new Set<DealerType>(
  Object.values(dealerParentageRules)
    .map((r) => r.allowedParent)
    .filter((t): t is DealerType => t !== null),
);

export const canOwnChildren = (type: DealerType): boolean =>
  ownableParentTypes.has(type); // => dealer_group, oem_group

// Franchise axis (7.1.1 DealerType::canFranchise() / canBeFranchised()).
// An oem is the franchisor; a franchise_dealer is the franchisee. This axis is
// separate from the ownership matrix above.
export const canFranchise = (type: DealerType): boolean => type === 'oem';
export const canBeFranchised = (type: DealerType): boolean => type === 'franchise_dealer';
